<template>
  <div class="account animated fadeIn">
    <b-row cols="1" cols-lg="2">
      <b-col class="mb-4">
        <b-card class="mb-0 h-100">
          <h2 class="text-center">Account Details</h2>

          <form @submit.prevent="onSubmit('account')" class="animated fadeIn">
            <NanInput
              :value="account.unique"
              label="Nanome ID"
              disabled
              class="mb-2"
            />

            <NanInput
              v-model="$v.account.username.$model"
              label="Username"
              :validation="$v.account.username"
              :disabled="!account.isEditing || isAdmin"
              class="mb-2"
            />

            <NanInput
              v-model="$v.account.email.$model"
              type="email"
              label="Email"
              :validation="$v.account.email"
              :disabled="!account.isEditing || isAdmin"
              class="mb-2"
            />

            <NanInput
              v-if="!account.isEditing"
              value=""
              placeholder="••••••••••••"
              label="Password"
              disabled
              class="mb-2"
            />

            <fieldset v-else>
              <legend class="sr-only">Change Password:</legend>

              <NanInput
                v-model="account.newPass"
                type="password"
                label="New Password"
                :validation="$v.account.newPass"
                :disabled="!account.isEditing"
                @change="$v.account.newPass.$touch()"
                class="mb-2"
              />

              <NanInput
                v-model="account.repeat"
                type="password"
                label="Repeat Password"
                :validation="$v.account.repeat"
                :disabled="!account.isEditing"
                @change="$v.account.repeat.$touch()"
                class="mb-2"
              />
            </fieldset>

            <b-button
              id="account-account_details-edit"
              @click="toggleEdit('account')"
              type="button"
              class="mr-2"
              >{{ account.isEditing ? "Cancel" : "Edit" }}</b-button
            >
            <b-button
              v-if="account.isEditing"
              id="account-account_details-submit"
              @click="promptPassword"
              :class="{
                disabled: !$v.account.$anyDirty || $v.account.$invalid
              }"
              variant="primary"
              type="button"
              >Submit</b-button
            >

            <b-modal v-model="requestPassword" centered hide-header hide-footer>
              <b-form @submit.prevent="onSubmit('account')">
                <NanInput
                  v-model="currentPass"
                  type="password"
                  label="Please verify your current password"
                  :validation="$v.currentPass"
                  class="mb-2"
                />

                <b-button
                  id="account-account_details-submit"
                  :class="{ disabled: $v.currentPass.$invalid }"
                  variant="primary"
                  type="submit"
                  >Submit</b-button
                >
              </b-form>
            </b-modal>

            <!-- TODO: Preferred language -->
          </form>
        </b-card>
      </b-col>

      <b-col class="mb-4">
        <b-card class="mb-0 h-100">
          <h2 class="text-center">User Details</h2>

          <b-form @submit.prevent="onSubmit('meta')">
            <NanInput
              v-model="meta.fullName"
              label="Full Name"
              :disabled="!meta.isEditing"
              autocomplete="name"
              class="mb-2"
            />

            <NanInput
              v-model="meta.altEmail"
              type="email"
              label="Alternate Email"
              :validation="$v.meta.altEmail"
              :disabled="!meta.isEditing"
              autocomplete="email"
              class="mb-2"
            />

            <NanInput
              :value="meta.phone | phone"
              @keydown="enforceFormat"
              @keyup="updatePhone"
              type="tel"
              label="Phone"
              :disabled="!meta.isEditing"
              autocomplete="tel"
              class="mb-2"
            />

            <b-button
              id="account-user_details-edit"
              @click="toggleEdit('meta')"
              type="button"
              class="mr-2"
              >{{ meta.isEditing ? "Cancel" : "Edit" }}</b-button
            >
            <b-button
              id="account-user_details-submit"
              v-if="meta.isEditing"
              :class="{ disabled: $v.meta.$invalid }"
              variant="primary"
              type="submit"
              >Submit</b-button
            >
          </b-form>
        </b-card>
      </b-col>
    </b-row>

    <b-row cols="1" cols-lg="3" class="text-center">
      <b-col class="mb-4">
        <b-card class="mb-0 h-100">
          <h2>Security</h2>
          <template v-if="user.tfa_enabled">
            <div class="mt-2 font-lg"><i class="fa fa-lock" /> 2FA enabled</div>
            <b-button
              v-if="canDisable2FA"
              class="mt-2 px-4"
              variant="danger"
              @click="disable2FA"
            >
              Disable 2FA
            </b-button>
            <br />
            <b-button v-b-modal.tfa-recovery-codes variant="link" size="sm">
              Recovery Codes
            </b-button>
          </template>
          <template v-else>
            <div class="mt-2 font-lg">
              <i class="fa fa-warning" /> 2FA not enabled
            </div>
            <b-button v-b-modal.tfa-setup class="mt-2 px-4" variant="primary">
              Enable 2FA
            </b-button>
          </template>

          <div class="mt-4">
            <div class="font-lg">Log out of other sessions</div>
            <b-btn @click="logOutOthers" class="mt-2 px-4" variant="warning">
              <i class="fa fa-lock" /> Log Out
            </b-btn>
          </div>
        </b-card>
      </b-col>

      <b-col class="mb-4">
        <b-card class="mb-0 h-100">
          <h2 class="mb-3">Emails</h2>
          <NanInput
            v-model="settings.email_chat"
            @change="onSubmit('settings')"
            type="checkbox"
            label="Notify me of unread chat messages"
            class="mb-3 text-left"
          />
          <NanInput
            v-model="settings.email_newsletter"
            @change="onSubmit('settings')"
            type="checkbox"
            label="Notify me of product updates and news"
            class="mb-3 text-left"
          />
          <p class="mt-4 small">
            To read our Privacy Policy, Terms of Service, and End User License
            Agreement, visit
            <a href="https://nanome.ai/terms">https://nanome.ai/terms</a>.
          </p>
        </b-card>
      </b-col>

      <b-col class="mb-4">
        <b-card class="mb-0 h-100">
          <h2 class="mb-3">Parental Controls</h2>
          <p>
            Restrict Nanome Web Browser content with a 4 digit passcode.
            Requires Nanome 1.24 or later.
          </p>
          <div>
            <i
              class="fa fa-circle mr-1"
              :class="
                user.parental_controls_enabled ? 'text-success' : 'text-danger'
              "
            />
            Browser Lock is {{ user.parental_controls_enabled ? "on" : "off" }}
          </div>
          <b-btn
            v-b-modal.parental-controls
            class="mt-2 px-4"
            variant="primary"
          >
            <i class="fa fa-shield" /> Settings
          </b-btn>
        </b-card>
      </b-col>
    </b-row>

    <Setup2FA />
    <GenerateRecoveryCodes />

    <b-modal
      ref="parental"
      id="parental-controls"
      size="sm"
      centered
      hide-header
      hide-footer
    >
      <ParentalControls @hide="() => $refs.parental.hide()" />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { validationMixin } from "vuelidate";
import {
  email,
  maxLength,
  minLength,
  required,
  sameAs
} from "vuelidate/lib/validators";
import { password } from "@/helpers/validators";

import GenerateRecoveryCodes from "@/components/2fa/GenerateRecoveryCodes";
import ParentalControls from "@/components/ParentalControls";
import Setup2FA from "@/components/2fa/Setup2FA";

export default {
  metaInfo: {
    titleTemplate: "My Account - %s"
  },

  mixins: [validationMixin],

  components: {
    GenerateRecoveryCodes,
    ParentalControls,
    Setup2FA
  },

  data: () => ({
    account: {
      unique: "",
      username: "",
      email: "",
      newPass: "",
      repeat: "",
      isEditing: false
    },

    requestPassword: false,
    currentPass: "",

    meta: {
      fullName: "",
      altEmail: "",
      phone: "",
      isEditing: false
    },

    settings: {
      email_chat: false,
      email_newsletter: false
    }
  }),

  computed: {
    ...mapGetters(["isAdmin"]),
    ...mapState(["user", "pendingRequests"]),

    canDisable2FA() {
      const { organization } = this.user;
      return !organization || !organization.settings.require_2fa;
    }
  },

  created() {
    this.syncUser();
  },

  methods: {
    syncUser() {
      this.account.unique = this.user.unique;
      this.account.username = this.user.name;
      this.account.email = this.user.email;
      this.account.currentPass = "";
      this.account.newPass = "";
      this.account.repeat = "";

      this.meta.fullName = this.user.meta.fullName;
      this.meta.altEmail = this.user.meta.altEmail;
      this.meta.phone = this.user.meta.phone;

      Object.assign(this.settings, this.user.settings);
    },

    toggleEdit(key) {
      this[key].isEditing = !this[key].isEditing;
      this.$v[key].$reset();
      this.syncUser();
    },

    promptPassword() {
      this.$v.account.$touch();
      if (this.$v.account.$invalid) return;
      if (this.isAdmin) {
        this.onSubmit("account");
      } else {
        this.requestPassword = true;
      }
    },

    async onSubmit(key) {
      if (this.pendingRequests) return;
      if (!["account", "meta", "settings"].includes(key)) return;

      if (this.$v[key].$invalid) {
        this.$v[key].$touch();
        return;
      }

      try {
        let success;
        if (key === "account") {
          // In case user triggers submit with 'enter' key in account details
          if (!this.requestPassword && !this.isAdmin) {
            this.requestPassword = true;
            return;
          }

          if (this.isAdmin) {
            const h = this.$createElement;
            const ok = await this.$bvModal.msgBoxConfirm(
              [
                h(
                  "p",
                  "Are you sure you want to overwrite the password for this user?"
                ),
                h("p", "New password:"),
                h("pre", this.account.newPass)
              ],
              {
                centered: true,
                okTitle: "Overwrite Password",
                okVariant: "danger",
                title: "Overwrite Password",
                headerBgVariant: "danger"
              }
            );
            if (!ok) return;
          }

          this.requestPassword = false;

          const newUser = {
            name: this.account.username,
            email: this.account.email,
            pass: this.account.newPass
          };

          success = await this.$store.dispatch("user/edit", {
            admin: this.isAdmin,
            pass: this.currentPass,
            data: newUser
          });
        } else if (key === "meta") {
          success = await this.$store.dispatch("user/editMeta", {
            fullName: this.meta.fullName,
            altEmail: this.meta.altEmail,
            phone: this.meta.phone
          });
        } else if (key === "settings") {
          success = await this.$store.dispatch(
            "user/editSettings",
            this.settings
          );
        }

        if (success) {
          this.$notify({
            type: "success",
            text: "Account updated"
          });
          this.syncUser();
        }
      } catch (err) {
        console.warn(err);
      } finally {
        this.toggleEdit(key);
        this.currentPass = "";
      }
    },

    async logOutOthers() {
      const confirm = await this.$bvModal.msgBoxConfirm(
        "This will log you out of all other sessions, including VR sessions. You will remain logged in to this current session.",
        {
          title: "Log Out Other Sessions",
          okTitle: "Confirm",
          okVariant: "warning",
          size: "sm",
          centered: true
        }
      );

      if (!confirm) return;

      try {
        const success = await this.$store.dispatch("user/logoutOthers");
        if (!success) return;
        this.$notify({
          type: "success",
          text: "Logged out of other sessions."
        });
      } catch (err) {
        console.error(err);
      }
    },

    async disable2FA() {
      const confirm = await this.$bvModal.msgBoxConfirm(
        "Are you sure you want to disable 2FA?",
        {
          title: "Disable 2FA",
          okTitle: "Confirm",
          okVariant: "danger",
          size: "sm",
          centered: true
        }
      );
      if (!confirm) return;

      try {
        const success = await this.$store.dispatch("user/disable2FA");
        if (!success) return;
        this.$notify({
          type: "success",
          text: "2FA disabled."
        });
      } catch (err) {
        console.error(err);
      }
    },

    // https://stackoverflow.com/questions/30058927/format-a-phone-number-as-a-user-types-using-pure-javascript
    isNumberKey(event) {
      const key = event.keyCode;
      return (
        (key >= 48 && key <= 57) || // Allow number line
        (key >= 96 && key <= 105) // Allow number pad
      );
    },
    isModifierKey(event) {
      const key = event.keyCode;
      // prettier-ignore
      return (
        event.shiftKey === true ||
        key === 35 ||
        key === 36 || // Allow Shift, Home, End
        (key === 8 || key === 9 || key === 13 || key === 46) || // Allow Backspace, Tab, Enter, Delete
        (key > 36 && key < 41) || // Allow left, up, right, down
        // Allow Ctrl/Command + A,C,V,X,Z
        ((event.ctrlKey === true || event.metaKey === true) &&
          (key === 65 || key === 67 || key === 86 || key === 88 || key === 90))
      );
    },
    enforceFormat(event) {
      // Input must be of a valid number format or a modifier key, and not longer than ten digits
      if (!this.isNumberKey(event) && !this.isModifierKey(event)) {
        event.preventDefault();
      }
    },
    updatePhone(event) {
      this.meta.phone = event.target.value.replace(/\D/g, "");
    }
  },

  validations: {
    account: {
      username: { required, min: minLength(6) },
      email: { required, email },
      newPass: {
        minLength: minLength(8),
        maxLength: maxLength(250),
        password
      },
      repeat: { matches: sameAs("newPass") }
    },
    currentPass: { required, minLength: minLength(8) },
    meta: {
      altEmail: { email }
    },
    settings: {
      // Required for the onSubmit validation check
    }
  }
};
</script>
